/**
 * Renders a compiled PDF template in the real @react-pdf-viewer with the detected
 * fill areas drawn as percent-of-page highlight boxes — the PDF-native counterpart
 * to the compiled raster geometry. Used by TemplateDetailPanel for PDF templates.
 *
 * The plugin wiring mirrors PdfSourceModal exactly: the pdf.js Worker is provided
 * app-globally (main.tsx), plugins are instantiated at top level (never in useMemo —
 * a stale-deps trap; see DocumentViewerPanel precedent), and renderHighlights reads
 * the latest areas through a ref so the plugin isn't recreated per render.
 *
 * Coordinate contract: a slot's `page_rect` is `[x0,y0,x1,y1]` as PERCENT of its whole
 * PDF page and `pdf_page` is the 0-indexed page — both computed server-side. The
 * highlight plugin wants left/top/width/height percentages, so width/height are the
 * span, not the far edge. Slots without PDF geometry (rect-less fallbacks) are skipped.
 *
 * Every fill area uses the same emerald treatment regardless of model confidence.
 */
import { useMemo, useRef } from "react";
import { Viewer } from "@react-pdf-viewer/core";
import {
    highlightPlugin,
    type RenderHighlightsProps,
    Trigger,
} from "@react-pdf-viewer/highlight";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { AlertCircle } from "lucide-react";

import type { SlotTarget } from "@/api";
import { Button } from "@/components/ui/button";
import { ViewerToolbar } from "@/components/shared/ViewerToolbar";
import { cn } from "@/lib/utils";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const MAX_PDF_RETRIES = 3;

interface TemplateHighlightArea {
    slotId: string;
    label: string | null;
    pageIndex: number;
    left: number;
    top: number;
    width: number;
    height: number;
}

function toHighlightAreas(slotTargets: SlotTarget[]): TemplateHighlightArea[] {
    const areas: TemplateHighlightArea[] = [];
    for (const slot of slotTargets) {
        if (slot.pdf_page == null || slot.page_rect == null) continue;
        const [x0, y0, x1, y1] = slot.page_rect;
        areas.push({
            slotId: slot.slot_id,
            label: slot.label,
            pageIndex: slot.pdf_page,
            left: x0,
            top: y0,
            width: x1 - x0,
            height: y1 - y0,
        });
    }
    return areas;
}

interface TemplatePdfViewerProps {
    /** Version-matched presigned GET of the original PDF. */
    pdfUrl: string | null;
    /** The preview's fill-role slots; only those with PDF geometry are drawn. */
    slotTargets: SlotTarget[];
    templateName: string;
    /** Refetch the owning detail query to rescue an expired presign. */
    onPdfRefetch: () => void;
    className?: string;
}

export function TemplatePdfViewer({
    pdfUrl,
    slotTargets,
    templateName,
    onPdfRefetch,
    className,
}: TemplatePdfViewerProps) {
    const highlightAreas = useMemo(
        () => toHighlightAreas(slotTargets),
        [slotTargets]
    );
    // Ref so renderHighlights reads the latest areas without recreating the plugin.
    const areasRef = useRef<TemplateHighlightArea[]>(highlightAreas);
    areasRef.current = highlightAreas;

    // A loaded document has a working presign right now, so restore the full retry
    // budget: a passive viewer left open past the presign TTL can then still recover on
    // a later expiry. A genuinely broken PDF never loads, so it stops at the cap.
    const retriesRef = useRef(0);

    // Plugin factories call React hooks internally; top-level only (never useMemo).
    const highlightPluginInstance = highlightPlugin({
        renderHighlights: (props: RenderHighlightsProps) => (
            <div>
                {areasRef.current
                    .filter((area) => area.pageIndex === props.pageIndex)
                    .map((area) => (
                        <div
                            key={area.slotId}
                            data-testid={`template-pdf-overlay-${area.slotId}`}
                            tabIndex={0}
                            aria-label={area.label ?? area.slotId}
                            className="group pointer-events-auto rounded-sm border-2 border-emerald-500/70 bg-emerald-400/10 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none"
                            style={props.getCssProperties(
                                {
                                    pageIndex: area.pageIndex,
                                    left: area.left,
                                    top: area.top,
                                    width: area.width,
                                    height: area.height,
                                },
                                props.rotation
                            )}
                        >
                            <span className="pointer-events-none absolute -top-5 left-0 flex max-w-[180px] items-center gap-1 truncate rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap text-white opacity-0 group-focus-within:opacity-100 group-hover:opacity-100">
                                {area.label ?? area.slotId}
                            </span>
                        </div>
                    ))}
            </div>
        ),
        trigger: Trigger.None,
    });
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar: ViewerToolbar,
        sidebarTabs: () => [],
    });

    return (
        <div
            data-testid="template-pdf-viewer"
            // A plain block (not a flex parent) so react-pdf-viewer's own
            // height:100% root fills it and owns the internal scroll — the caller
            // hands it a definite height via `flex-1 min-h-0`. Mirrors
            // PdfSourceModal's `<main className="flex-1 overflow-hidden">`.
            className={cn(
                "bg-muted/30 overflow-hidden rounded-md border",
                className
            )}
        >
            {pdfUrl ? (
                <Viewer
                    // Key on the URL so a fresh presign remounts the viewer cleanly and
                    // GCs the prior pdf.js document (a decoded PDF is heavy).
                    key={pdfUrl}
                    fileUrl={pdfUrl}
                    plugins={[
                        highlightPluginInstance,
                        defaultLayoutPluginInstance,
                    ]}
                    onDocumentLoad={() => {
                        retriesRef.current = 0;
                    }}
                    renderError={() => {
                        const canAutoRetry =
                            retriesRef.current < MAX_PDF_RETRIES;
                        return (
                            <PdfLoadError
                                templateName={templateName}
                                canAutoRetry={canAutoRetry}
                                onAutoRetry={() => {
                                    retriesRef.current += 1;
                                    onPdfRefetch();
                                }}
                                onManualRetry={() => {
                                    retriesRef.current = 0;
                                    onPdfRefetch();
                                }}
                            />
                        );
                    }}
                />
            ) : (
                <PdfLoadError
                    templateName={templateName}
                    canAutoRetry={false}
                    onAutoRetry={() => {}}
                    onManualRetry={onPdfRefetch}
                />
            )}
        </div>
    );
}

function PdfLoadError({
    templateName,
    canAutoRetry,
    onAutoRetry,
    onManualRetry,
}: {
    templateName: string;
    canAutoRetry: boolean;
    onAutoRetry: () => void;
    onManualRetry: () => void;
}) {
    // Fire the bounded auto-refetch exactly once on mount — a load failure is most often
    // an expired presign, and a fresh detail read rotates it. Remounting on the new URL
    // (Viewer key) resets this, so the budget in the parent is what bounds the loop.
    const fired = useRef(false);
    if (canAutoRetry && !fired.current) {
        fired.current = true;
        // Defer out of render so the refetch's state update doesn't fire mid-render.
        queueMicrotask(onAutoRetry);
    }
    return (
        <div
            data-testid="template-pdf-error"
            className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center"
        >
            <AlertCircle className="text-muted-foreground size-8" aria-hidden />
            <div className="max-w-xs">
                <p className="text-sm font-medium">
                    Couldn&apos;t load this PDF
                </p>
                <p className="text-muted-foreground mt-1 text-sm">
                    The preview of {templateName} didn&apos;t render. Retry to
                    fetch a fresh link.
                </p>
            </div>
            <Button variant="outline" size="sm" onClick={onManualRetry}>
                Retry
            </Button>
        </div>
    );
}
